/**
 * Resolve an absolute URL for a first-party API path (e.g. `/api/morrow/...`).
 *
 * The hosted web app (app.morrowos.com) and the API (api.morrowos.com) are
 * different origins. A bare `fetch("/api/morrow/...")` therefore resolves
 * against the *Pages* origin (app.morrowos.com), which has no such route —
 * it 404s/405s and the connect/usage/memory calls silently fail. Prefix these
 * paths with the API origin from `VITE_HTTP_URL` so they reach the backend
 * (and so installApiFetchAuth's fetch patch, which keys on the API origin,
 * attaches the Clerk/owner auth headers).
 *
 * On DESKTOP there is no `VITE_HTTP_URL`; the backend is a local loopback
 * process the renderer reaches over a desktop-managed target, and the renderer
 * itself is served from a custom scheme (morrowos://). A relative path would
 * resolve against that scheme and hit the renderer bundle (404), so we prefix
 * the desktop backend's origin instead.
 *
 * When neither is available (local same-origin dev), we return the path
 * unchanged so the request stays same-origin as before.
 */
const apiOrigin = (() => {
  const raw = import.meta.env.VITE_HTTP_URL?.trim();
  if (!raw) return null;
  try {
    return new URL(raw).origin;
  } catch {
    return null;
  }
})();

type DesktopBridge = {
  getLocalEnvironmentBootstraps?: () => ReadonlyArray<{ httpBaseUrl?: string | null }>;
};

/**
 * The local backend origin on desktop, read from the desktop bridge's
 * environment bootstraps (same source as environments/primary/target.ts). Null
 * off desktop or before the bootstrap is available.
 */
export function desktopApiOrigin(): string | null {
  try {
    if (typeof window === "undefined") return null;
    const bridge = (window as { desktopBridge?: DesktopBridge }).desktopBridge;
    if (!bridge?.getLocalEnvironmentBootstraps) return null;
    const base = bridge.getLocalEnvironmentBootstraps().find((b) => b.httpBaseUrl)?.httpBaseUrl;
    return base ? new URL(base).origin : null;
  } catch {
    return null;
  }
}

/** Prefix a root-relative API path with the API origin, when one is available. */
export function apiUrl(path: string): string {
  const withPath = (origin: string): string =>
    `${origin}${path.startsWith("/") ? path : `/${path}`}`;
  if (apiOrigin !== null) return withPath(apiOrigin);
  const desktop = desktopApiOrigin();
  if (desktop !== null) return withPath(desktop);
  return path;
}
